import type { Language } from '@eformulieren/engine'
import { BusyButton } from '@eformulieren/renderer'
import { type ReactNode, useId, useState } from 'react'

/**
 * Niet-opgeslagen invoer, zichtbaar en te kopiëren (besluit 9 van 30 oktober 2026, audit delen G-01,
 * mockup 08). Een tekstvak dat alleen te lezen is, zodat selecteren ook zonder de knop werkt, en een knop
 * "Kopiëren" in het patroon van `BusyButton`: bezig zolang het klembord schrijft, daarna "Gekopieerd." in
 * een statusregel, en bij een weigering van de browser een melding aan de knop.
 */
const T = {
  kopieer: { nl: 'Kopiëren', en: 'Copy' },
  bezig: { nl: 'Bezig met kopiëren…', en: 'Copying…' },
  klaar: { nl: 'Gekopieerd.', en: 'Copied.' },
  mislukt: {
    nl: 'Kopiëren lukte niet. Selecteer de tekst en kopieer hem zelf.',
    en: 'Copying did not work. Select the text and copy it yourself.',
  },
} as const

export function CopyBox({ taal: language, label, uitleg, tekst }: { readonly taal: Language; readonly label: string; readonly uitleg?: string; readonly tekst: string }): ReactNode {
  const id = useId()
  const [copied, setCopied] = useState(false)
  return (
    <div className="pod-kopie" data-proef="niet-opgeslagen">
      <label htmlFor={id} className="pod-kopie__label">
        {label}
      </label>
      {uitleg === undefined ? null : <p className="pod-kopie__uitleg">{uitleg}</p>}
      <textarea id={id} className="ef-invoer ef-invoer--meerregelig" readOnly rows={Math.min(8, Math.max(2, tekst.split('\n').length + 1))} value={tekst} />
      <div className="pod-kopie__acties">
        <BusyButton
          className="ef-actieknop"
          data-soort="secundair"
          data-proef="kopieer"
          busyText={T.bezig[language]}
          errorText={T.mislukt[language]}
          onAction={async () => {
            setCopied(false)
            await navigator.clipboard.writeText(tekst)
            setCopied(true)
          }}
        >
          {T.kopieer[language]}
        </BusyButton>
        <span role="status" className="pod-kopie__klaar">
          {copied ? T.klaar[language] : ''}
        </span>
      </div>
    </div>
  )
}
